<script setup lang="ts">
import 'vue-loading-overlay/dist/css/index.css'
import { toTypedSchema } from '@vee-validate/zod'
import { Loader2, TriangleAlert } from 'lucide-vue-next'
import { configure, useForm } from 'vee-validate'
import { computed, defineComponent, h, nextTick, onMounted, reactive, ref, shallowRef, watch } from 'vue'
import LoadingOverlay from 'vue-loading-overlay'
import * as z from 'zod'
import ContactQuickCreateDialog from '@/components/ContactQuickCreateDialog.vue'
import DialogCheck from '@/components/DialogCheck.vue'
import { useErrorMessage, useFiles } from '@/composables'
import { useColorMode } from '@/composables/src/useColorMode'
import { Alert, AlertDescription } from '@/foundation/alert'
import { AutoForm } from '@/foundation/auto-form'
import { Button } from '@/foundation/button'
import { getProductsByCustomerId } from '@/service/products'
import { getUser } from '@/service/users.ts'
import type { Company } from '@/types/organization'
import type { Product } from '@/types/product'
import { eventBus } from '@/utils/utils'
import { ticketTypeIcon, ticketTypesColors } from '@/views/tickets/data/data'
import { DependencyType } from '../foundation/auto-form/interface'
import { createTickets } from '../service/tickets'
import { useAccountStore } from '../stores/account'
import { useCompanyStore } from '../stores/companies'
import { useTeamStore } from '../stores/teams'
import { useTicketStore } from '../stores/tickets'

type Props = {
  preFilledCompany?: Company | null
  preFilledProduct?: Product | null
  lockCompany?: boolean
  lockProduct?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  preFilledCompany: null,
  preFilledProduct: null,
  lockCompany: false,
  lockProduct: false,
})

configure({
  bails: false,
  validateOnBlur: false,
  validateOnChange: false,
  validateOnInput: false,
  validateOnModelUpdate: false,
})

const emit = defineEmits<{
  (e: 'dialogShouldClose'): void
  (e: 'closeDialog'): void
}>()

const ticketStore = useTicketStore()
const companyStore = useCompanyStore()
const teamStore = useTeamStore()
const accountStore = useAccountStore()
const { handleFilesChange } = useFiles()
const { fetchTeamMembers } = useTeamStore()
const user = ref({}) as any
const isLoading = ref(false)
const isSubmitting = ref(false)
const isCompanyContactLoading = ref(false)
const isTeamMembersLoading = ref(false)
const { mode } = useColorMode()
const teamMembers = ref<any[]>([])

// New computed property to check data readiness for client users
const isDataReadyForClient = computed(() => {
  if (!accountStore.isCustomerUser) return true
  const ready = accountStore.isCustomerUser && !!companyStore.companies && companyStore.companies.length > 0

  return ready
})

// Refs for the new contact dialog
const showCreateContactDialog = ref(false)
const selectedCompanyForNewContact = ref<{ id: string; name: string } | null>(null)

// Define ContactInfoDisplay
const ContactInfoDisplay = defineComponent({
  setup() {
    // user and isLoading are accessible from the parent CreateTicketForm's setup scope
    return () => {
      if (!user.value.name && !isLoading.value) return null

      return h('div', { class: 'relative' }, [
        h('div', { class: 'lg:absolute top-0 h-full left-full lg:ml-8' }, [
          h(
            'div',
            {
              class: `contact-info-display-wrapper ${errorMessage.value ? 'mt-24' : 'mt-3'} mb-3 w-2/5 lg:w-[400px] lg:fixed top-20`,
            },
            [
              user.value.name ? h('h2', { class: 'text-sm font-semibold mb-2' }, 'Contact Info') : null,
              h(LoadingOverlay, {
                active: isLoading.value,
                canCancel: false,
                color: 'black',
                isFullPage: false,
              }),
              !isLoading.value && user.value.name
                ? h('div', { class: 'card !bg-background-light rounded-lg p-6 py-3 border ' }, [
                    h('div', { class: 'mb-3' }, [
                      h('label', { class: 'block text-muted-foreground' }, 'Name:'),
                      h('span', { class: 'block text-muted-foreground' }, user.value.name),
                    ]),
                    h('div', { class: 'mb-3' }, [
                      h('label', { class: 'block text-muted-foreground' }, 'Phone:'),
                      h('span', { class: 'block text-muted-foreground' }, user.value?.phone),
                    ]),
                    h('div', { class: 'mb-3' }, [
                      h('label', { class: 'block text-muted-foreground' }, 'Email:'),
                      h('span', { class: 'block text-muted-foreground' }, user.value?.email),
                    ]),
                  ])
                : null,
            ],
          ),
        ]),
      ])
    }
  },
})

// Define Form Schema using zod
const schema = z.object({
  company: z.object(
    {
      id: z.string({ required_error: 'Company is required.' }),
      name: z.string(),
    },
    { required_error: 'Company is required.' },
  ),
  contact: z.object(
    {
      id: z.string({ required_error: 'Contact is required.' }),
      name: z.string(),
    },
    { required_error: 'Contact is required.' },
  ),
  contactInfo: z.any().optional(),
  ticketType: z.object(
    {
      id: z.string({ required_error: 'Ticket type is required.' }),
      name: z.string(),
    },
    { required_error: 'Ticket type is required.' },
  ),
  category: z
    .object({
      id: z.string().optional(),
      name: z.string(),
      parent: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .optional(),
    })
    .optional()
    .refine(
      (val) => {
        // Make category required only for non-clients
        if (accountStore.user?.role !== 'client') {
          return val && val.name != null // Required for non-clients
        }

        return true // Optional for clients
      },
      {
        message: 'Category is required.',
      },
    ),
  title: z
    .string({
      required_error: 'Ticket title is required.',
    })
    .min(1, { message: 'Ticket title is required.' })
    .max(200, { message: 'Title must not exceed 200 characters.' }),

  description: z
    .string({
      required_error: 'Ticket description is required.',
    })
    .min(1, { message: 'Ticket description is required.' })
    .refine(
      (value) => {
        // Custom validation logic
        return value !== '<p></p>' && value !== '<p><br></p>'
      },
      {
        message: 'Ticket description is required.',
      },
    ),

  product: z
    .object({
      id: z.string().optional(),
      name: z.string(),
    })
    .optional(),

  files: z.any().optional(),

  assignee: z.object(
    {
      id: z.string({ required_error: 'Team Assignee is required.' }),
      name: z.string(),
    },
    { required_error: 'Team Assignee is required.' },
  ),

  assigneeUser: z
    .object({
      id: z.string().optional(),
      name: z.string().optional(),
    })
    .optional(),

  priority: z.object(
    {
      id: z.string({ required_error: 'Priority is required.' }),
      name: z.string(),
    },
    { required_error: 'Priority is required.' },
  ),
})
const openNewContactDialog = () => {
  if (selectedCompanyForNewContact.value) {
    showCreateContactDialog.value = true
  } else {
    console.warn('Cannot create contact without a selected company.')
  }
}
const updateNameKey = ref(0)
// Reactive fieldConfig for dynamic updates (like options)
const fieldConfig = reactive({
  company: {
    label: 'Company Name',
    component: 'autocomplete',
    inputProps: {
      insideText: 'Companies',
      placeholder: 'Select Company Name',
      class: 'w-full',
      options: computed(() => companyStore.companies),
      emptyMessage: computed(() => {
        if (accountStore.isCustomerUser && (!accountStore.user?.organizations || !accountStore.user.organizations.length)) {
          return 'No companies available for your account. Please contact support.'
        }

        return 'No results found.'
      }),
      disabled: props.lockCompany,
    },
  },
  contact: {
    label: 'Contact Name',
    component: 'autocomplete',
    class: '!my-6',
    inputProps: {
      placeholder: 'Select Contact',
      selectedCompany: computed(() => selectedCompanyForNewContact.value),
      insideText: 'Contacts',
      disabled: true,
      class: 'w-full',
      emptyMessage: 'No contact found',
      options: [] as any[],
      showLogo: true,
      key: updateNameKey, // Use key to force re-render when new contact is added
      //add event listener to open new contact dialog
      onOpenNewContactDialog: async () => {
        updateNameKey.value++
        await nextTick() // Ensure reactivity is settled before opening dialog
        openNewContactDialog()
      },
    },
  },
  contactInfo: {
    component: shallowRef(ContactInfoDisplay),
    class: '',
  },
  ticketType: {
    label: 'Ticket Type',
    component: 'autocomplete',
    inputProps: {
      placeholder: 'Select Ticket Type',
      insideText: 'Ticket Types',
      class: 'w-full',
      showIcon: true,
      options: computed(() =>
        ticketStore.types.map((type) => ({
          ...type,
          icon: ticketTypeIcon[type.icon],
          bg: ticketTypesColors[type.color],
        })),
      ),
      defaultValue: computed(() => {
        const requestType = ticketStore.types.find((t) => t.name.toLowerCase() === 'request')

        if (requestType) {
          return {
            id: requestType.id,
            name: requestType.name,
            icon: ticketTypeIcon[requestType.icon],
            bg: ticketTypesColors[requestType.color],
          }
        }

        return {
          id: undefined,
          name: undefined,
        }
      }),
    },
  },
  category: {
    label: 'Category',
    component: 'autocomplete',
    inputProps: {
      placeholder: 'Category',
      insideText: 'Categories',
      options: ticketStore.categories,
      class: 'w-full',
      emptyMessage: 'No category found',
    },
  },
  product: {
    label: 'Project',
    component: 'autocomplete',
    inputProps: {
      placeholder: 'Select Project',
      insideText: 'Projects',
      options: [] as any[],
      class: 'w-full',
      emptyMessage: 'No project found',
      disabled: true,
    },
  },
  title: {
    label: 'Ticket Title',
    maxLength: 100,
    inputProps: {
      class: '!w-full',
      placeholder: 'Add a Ticket title',
    },
  },

  description: {
    label: 'Description',
    maxLength: 2000,
    component: 'ticketDescription',
    showCharacterCount: false,
    inputProps: {
      class: 'w-full',
      placeholder: 'Type your message here',
      rows: 5,
    },
  },
  files: {
    label: 'Attachment',
    component: 'file',
    inputProps: {
      class: `!w-full ${mode.value === 'dark' ? 'border-sidebar-accent [&>_p]:text-secondary-foreground [&>_button]:text-primary-foreground' : ''}`,
    },
  },
  assignee: {
    label: 'Assignee Team',
    component: 'autocomplete',
    inputProps: {
      placeholder: 'Select Assignee Team',
      insideText: 'Teams',
      class: 'w-full',
      options: teamStore.teams,
      defaultValue: computed(() => {
        const requestType = ticketStore.types.find((t) => t.name.toLowerCase() === 'request')
        const defaultTeamId = requestType?.defaultTeamId

        if (defaultTeamId) {
          const foundTeam = teamStore.teams.find((team) => team.id === defaultTeamId)

          if (foundTeam) {
            return {
              id: foundTeam.id,
              name: foundTeam.name,
            }
          }
        }

        return {
          id: undefined,
          name: undefined,
        }
      }),
    },
  },
  assigneeUser: {
    label: 'Assignee User',
    component: 'autocomplete',
    inputProps: {
      placeholder: computed(() => (isTeamMembersLoading.value ? 'Loading team members...' : 'Select Assignee User')),
      insideText: 'Users',
      class: 'w-full',
      options: teamMembers,
      disabled: true,
      emptyMessage: 'No team member found',
    },
  },
  priority: {
    label: 'Priority',
    component: 'autocomplete',
    inputProps: {
      placeholder: 'Select Priority',
      insideText: 'Priorities',
      class: 'w-full',
      options: ticketStore.priorities,
      defaultValue: computed(
        () =>
          ticketStore.types.find((t) => t.name.toLowerCase() === 'request')?.defaultPriority ?? {
            id: undefined,
            name: undefined,
          },
      ),
    },
  },
})

const form = useForm({
  validationSchema: toTypedSchema(schema),
  initialValues: {
    company: props.preFilledCompany
      ? {
          id: props.preFilledCompany.id,
          name: props.preFilledCompany.name,
        }
      : undefined,
    product: props.preFilledProduct
      ? {
          id: props.preFilledProduct.id,
          name: props.preFilledProduct.name,
        }
      : undefined,
    ticketType: (() => {
      const requestType = ticketStore.types.find((t) => t.name.toLowerCase() === 'request')

      if (requestType) {
        return {
          id: requestType.id,
          name: requestType.name,
          icon: ticketTypeIcon[requestType.icon],
          bg: ticketTypesColors[requestType.color],
        }
      }

      return {
        id: undefined,
        name: undefined,
      }
    })(),
    priority: ticketStore.types.find((t) => t.name.toLowerCase() === 'request')?.defaultPriority ?? {
      id: undefined,
      name: undefined,
    },
    assignee: (() => {
      const requestType = ticketStore.types.find((t) => t.name.toLowerCase() === 'request')
      const defaultTeamId = requestType?.defaultTeamId

      if (defaultTeamId) {
        const foundTeam = teamStore.teams.find((team) => team.id === defaultTeamId)

        if (foundTeam) {
          return {
            id: foundTeam.id,
            name: foundTeam.name,
          }
        }
      }

      return {
        id: undefined,
        name: undefined,
      }
    })(),
  },
})

// Helper function to process fetched contacts based on user role
async function processCompanyContactsBasedOnRole(contacts: any[]) {
  if (accountStore.isCustomerUser) {
    const currentRole = accountStore.userRole
    const currentUser = accountStore.user

    if (currentRole === 'client') {
      const userContact = contacts.find(
        (contact: any) =>
          contact.id === currentUser.id || contact.userId === currentUser.id || contact.email === currentUser.email,
      )

      if (userContact) {
        fieldConfig.contact.inputProps.options = [userContact]
        form.setFieldValue('contact', userContact)
        user.value = {
          id: userContact.id,
          name: userContact.name,
          email: userContact.email,
          phone: userContact.phone || userContact.primaryPhone,
        }
      } else {
        fieldConfig.contact.inputProps.options = []
        fieldConfig.contact.inputProps.emptyMessage = 'No contact found. Please contact your administrator.'
      }
    } else if (currentRole === 'client_manager' || currentRole === 'client_approver') {
      fieldConfig.contact.inputProps.options = contacts
      const selfContact = contacts.find(
        (contact: any) =>
          contact.id === currentUser.id || contact.userId === currentUser.id || contact.email === currentUser.email,
      )

      if (selfContact) {
        form.setFieldValue('contact', selfContact)
        user.value = {
          id: selfContact.id,
          name: selfContact.name,
          email: selfContact.email,
          phone: selfContact.phone || selfContact.primaryPhone,
        }
      }
    }
  } else {
    // Non-customer users (agent, manager, admin) see all contacts
    fieldConfig.contact.inputProps.options = contacts
  }
  fieldConfig.contact.inputProps.disabled = false
}

// Dependencies Configuration
const dependencies = [
  {
    sourceField: 'company',
    type: DependencyType.DISABLES,
    targetField: 'contact',
    async when(value: any) {
      // Reset contact selection when company changes
      form.setFieldValue('contact', undefined, false)
      // Clear any validation error for contact
      form.setFieldError('contact', '')
      user.value = {}
      fieldConfig.contactInfo.class = ''
      if (value?.id) {
        isCompanyContactLoading.value = true
        selectedCompanyForNewContact.value = { id: value.id, name: value.name }
        try {
          const fetchedContacts = (await companyStore.fetchCompanyContacts(value.id)) as unknown as any[]

          await processCompanyContactsBasedOnRole(fetchedContacts)
        } catch (error) {
          fieldConfig.contact.inputProps.options = []
          fieldConfig.contact.inputProps.disabled = true
        } finally {
          isCompanyContactLoading.value = false
        }
      } else {
        fieldConfig.contact.inputProps.disabled = true
        fieldConfig.contact.inputProps.options = [] // Clear options when company is deselected
        user.value = {}
        fieldConfig.contactInfo.class = ''
        selectedCompanyForNewContact.value = null
        isCompanyContactLoading.value = false // Ensure loader stops if company is cleared during load
      }

      // The return value of when() determines if the target field is disabled.
      // If value has an id (company selected), contact should NOT be disabled (return false).
      // If value has no id (company not selected), contact SHOULD be disabled (return true).
      return !value?.id
    },
  },
  {
    sourceField: 'company',
    type: DependencyType.DISABLES,
    targetField: 'product',
    async when(value: any) {
      // Don't reset product if it's locked (prefilled)
      if (!props.lockProduct) {
        form.setFieldValue('product', undefined, false)
        form.setFieldError('product', '')
      }

      if (value?.id) {
        try {
          const { data } = getProductsByCustomerId(value.id)

          // Wait for products to load
          const unwatchProducts = watch(
            () => data.value,
            (products) => {
              if (products) {
                unwatchProducts()
                fieldConfig.product.inputProps.options = products.map((p: any) => ({
                  id: p.id,
                  name: p.name,
                }))

                // If we have a prefilled product, add it to options and set it
                if (props.preFilledProduct) {
                  const prefilledOption = {
                    id: props.preFilledProduct.id,
                    name: props.preFilledProduct.name,
                  }

                  // Add prefilled product if not in list
                  if (!fieldConfig.product.inputProps.options.find((p: any) => p.id === prefilledOption.id)) {
                    fieldConfig.product.inputProps.options = [...fieldConfig.product.inputProps.options, prefilledOption]
                  }

                  // Set the value
                  form.setFieldValue('product', prefilledOption)
                }

                fieldConfig.product.inputProps.disabled = !!props.lockProduct
              }
            },
            { immediate: true },
          )
        } catch (error) {
          fieldConfig.product.inputProps.options = []
          fieldConfig.product.inputProps.disabled = true
        }
      } else {
        fieldConfig.product.inputProps.disabled = true
        fieldConfig.product.inputProps.options = []
      }

      return !value?.id
    },
  },
  {
    sourceField: 'category',
    type: DependencyType.HIDES, // This type might be misleading, it's more of a side-effect trigger
    targetField: 'category', // The target field isn't actually having its options set here
    when: () => {
      return accountStore.user?.role && typeof accountStore.user?.role === 'string' && accountStore.user?.role === 'client' // This dependency type doesn't typically disable anything
    },
  },
  {
    sourceField: 'category',
    type: DependencyType.REQUIRES, // This type might be misleading, it's more of a side-effect trigger
    targetField: 'category', // The target field isn't actually having its options set here
    when: () => {
      return accountStore.user?.role && typeof accountStore.user?.role === 'string' && accountStore.user?.role !== 'client' // This dependency type doesn't typically disable anything
    },
  },
  {
    sourceField: 'ticketType',
    type: DependencyType.SETS_OPTIONS,
    targetField: 'assignee',
    when: (value: any) => {
      if (value?.id) {
        const selectedType = ticketStore.types.find((t) => t.id === value.id)

        if (selectedType) {
          if (selectedType.defaultTeamId) {
            const foundTeam = teamStore.teams.find((team) => team.id === selectedType.defaultTeamId)

            if (foundTeam) {
              form.setFieldValue('assignee', {
                id: foundTeam.id,
                name: foundTeam.name,
              })
            }
          }

          if (selectedType.defaultPriority) {
            form.setFieldValue('priority', selectedType.defaultPriority)
          }
        }
      }

      return false
    },
  },
  {
    sourceField: 'contact',
    type: DependencyType.SETS_OPTIONS, // This type might be misleading, it's more of a side-effect trigger
    targetField: 'priority', // The target field isn't actually having its options set here
    when: (value: any) => {
      if (value?.id) {
        isLoading.value = true
        user.value = {} // Clear previous user info first
        fieldConfig.contactInfo.class = ''

        const apiCall = getUser(value.id)

        apiCall.onSuccess((data: any) => {
          user.value = data
          isLoading.value = false
        })

        if (typeof apiCall.onError === 'function') {
          apiCall.onError(() => {
            isLoading.value = false
            // Optionally clear user.value or show an error if getUser fails
            user.value = {}
          })
        }
      } else {
        user.value = {}
        fieldConfig.contactInfo.class = ''
        isLoading.value = false // Ensure loading stops if contact is cleared
      }

      return false // This dependency type doesn't typically disable anything
    },
  },
  {
    sourceField: 'assignee',
    type: DependencyType.DISABLES,
    targetField: 'assigneeUser',
    async when(value: any) {
      form.setFieldValue('assigneeUser', undefined, false)
      form.setFieldError('assigneeUser', '')

      if (value?.id) {
        teamMembers.value = []
        fieldConfig.assigneeUser.inputProps.options = []
        fieldConfig.assigneeUser.inputProps.disabled = true
        isTeamMembersLoading.value = true

        try {
          const members = await fetchTeamMembers(value.id)

          teamMembers.value = members || []
          fieldConfig.assigneeUser.inputProps.options = teamMembers.value
          fieldConfig.assigneeUser.inputProps.disabled = false
        } catch (error) {
          teamMembers.value = []
          fieldConfig.assigneeUser.inputProps.options = []
          fieldConfig.assigneeUser.inputProps.disabled = true
        } finally {
          isTeamMembersLoading.value = false
        }
      } else {
        teamMembers.value = []
        fieldConfig.assigneeUser.inputProps.options = []
        fieldConfig.assigneeUser.inputProps.disabled = true
        isTeamMembersLoading.value = false
      }

      return !value?.id
    },
  },
]

const initializeForm = async () => {
  if (isLoading.value && !accountStore.isCustomerUser) return

  isLoading.value = true

  try {
    if (accountStore.isCustomerUser) {
      let filteredCompanies: any[] = []
      const options = fieldConfig.company.inputProps.options

      if (options && typeof options === 'object' && 'value' in options) {
        filteredCompanies = Array.isArray(options.value) ? options.value : []
      }
      // Only auto-select if exactly one company is available
      if (filteredCompanies.length === 1) {
        const userCompany = filteredCompanies[0]

        form.setFieldValue('company', {
          id: userCompany.id,
          name: userCompany.name,
        })
      }
    }
  } catch (error) {
    console.error('[CreateTicketForm] Error initializing form for client user:', error)
  } finally {
    isLoading.value = false
  }
}

watch(
  [() => accountStore.user, () => companyStore.companies, isDataReadyForClient],
  ([dataReady], [oldDataReady]) => {
    if (accountStore.isCustomerUser) {
      if (dataReady) {
        if (!oldDataReady || !form.values.company?.id) {
          initializeForm()
        }
      } else {
        if (!isLoading.value) isLoading.value = true // Show loader if not already
      }
    } else {
      if (isLoading.value) isLoading.value = false // Stop general loading if it was on for client
    }
  },
  { deep: true },
)

watch(
  () => form.values,
  async () => {
    // React to changes in form values
    if (Object.entries(form.errorBag.value).length > 0) {
      await form.validate()
      await nextTick()
      const validationResult = await form.validate()

      if (!validationResult.valid) {
        setFormErrors() // Set specific field errors if validation fails
      }
    }
  },
  { deep: true },
)

// Watch for prefilled props changes and update form values
watch(
  [() => props.preFilledCompany, () => props.preFilledProduct],
  ([company]) => {
    if (company && (!form.values.company || form.values.company.id !== company.id)) {
      // Setting company will trigger the dependency which will load products and set the prefilled product
      form.setFieldValue('company', {
        id: company.id,
        name: company.name,
      })
    }
  },
  { immediate: true, deep: true },
)

onMounted(async () => {
  if (accountStore.isCustomerUser && !isDataReadyForClient.value) {
    isLoading.value = true // Show loading indicator
  } else if (accountStore.isCustomerUser && isDataReadyForClient.value) {
    await nextTick() // Ensure reactivity is settled
    initializeForm()
  } else {
    if (isLoading.value) isLoading.value = false // Ensure loader is off
  }
})

const handleDialogShouldClose = () => {
  emit('dialogShouldClose')
}

// Handle Form Submission
const onSubmit = async (values: Record<string, any>, closeDialog: boolean = true) => {
  // Explicitly check for assignee and its ID
  if (!values.assignee || !values.assignee.id) {
    form.setFieldError('assignee', 'Team Assignee is required.')
    isLoading.value = false // Ensure loading spinner stops if it was started
    console.error('Assignee ID is missing, submission blocked.')

    return // Block submission
  }

  isLoading.value = true
  const files = await handleFilesChange(
    values.files?.map((item: any) => item.file),
    'INTERNAL',
  )
  let categoryId = values.category?.id
  let subCategoryId = null

  if (values.category?.parent) {
    categoryId = values.category.parent.id
    subCategoryId = values.category.id
  }

  const ticketData: any = {
    title: values.title,
    description: values.description,
    categoryId,
    subCategoryId,
    typeId: values.ticketType.id,
    priorityId: values.priority.id,
    assignedToTeamId: values.assignee.id,
    customerId: values.contact.id,
    companyId: values.company.id,
    files,
    actualTime: new Date(),
  }

  if (values.assigneeUser?.id) {
    ticketData.assignedToId = values.assigneeUser.id
  }

  if (values.product?.id) {
    ticketData.productId = values.product.id
  }

  const { onSuccess, onError } = createTickets(ticketData)

  onSuccess(() => {
    // Emit event for views to re-fetch with their own filters instead of global fetch
    eventBus.emit('ticket-created')
    if (closeDialog) emit('closeDialog')
    isLoading.value = false
  })

  onError((data) => {
    form.setErrors({
      title: data.message || 'An error occurred while creating the ticket.',
    })
    isLoading.value = false
  })
}

const setFormErrors = () => {
  const requiredFields: Record<string, string> = {
    company: 'Company is required.',
    contact: 'Contact is required.',
    ticketType: 'Ticket type is required.',
    assignee: 'Team Assignee is required.',
    priority: 'Priority is required.',
  }

  for (const [field, message] of Object.entries(requiredFields)) {
    if (!(form.values as any)[field]?.id) {
      form.setFieldError(field as keyof typeof form.values, message)
    }
  }
}

// Submission handler that triggers validation for all fields
const submitForm = async () => {
  if (isLoading.value) return

  // Trigger validation on all fields and mark as submitting
  isSubmitting.value = true
  const validationResult = await form.validate()

  if (!validationResult.valid) {
    setFormErrors() // Set specific field errors if validation fails
    isSubmitting.value = false // Stop submitting state if validation fails

    return
  }

  // Proceed with ticket creation after passing validation
  await onSubmit(form.values)
}

const submitAndAddAnother = async () => {
  if (isLoading.value) return

  isSubmitting.value = true
  const validationResult = await form.validate()

  if (!validationResult.valid) {
    setFormErrors() // Set specific field errors if validation fails
    isSubmitting.value = false // Stop submitting state if validation fails

    return
  }
  await onSubmit(form.values, false) // Submit without closing the dialog
  //clear form values for a new ticket
  form.resetForm()
  formKey.value += 1 // Force re-render of the form by changing the key
}

const { errorMessage } = useErrorMessage(isSubmitting, form.errors)

// Function to open the create contact dialog

const formKey = ref(0)
// Handle newly created contact
const handleContactCreated = async (newContactData: any) => {
  showCreateContactDialog.value = false // Explicitly close the dialog

  if (!newContactData || !newContactData.id || !newContactData.name) {
    return
  }

  const newlyCreatedContactId = newContactData.id
  const currentCompanyId = form.values.company?.id

  if (currentCompanyId) {
    try {
      companyStore.addCompanyContacts([newContactData])
      const freshContactsRaw = [...companyStore.companyContacts]

      // Map fresh contacts to the structure needed for dropdown and display
      const mappedContactOptions = freshContactsRaw.map((c: any) => ({
        id: c.id,
        name: c.name,
        email: c.email, // For ContactInfoDisplay and potentially autocomplete details
        phone: c.primaryPhone || c.phone, // Prioritize primaryPhone, fallback to phone if it exists
        profile: c.profile, // For ContactInfoDisplay (address might be here)
        // Add other fields if ContactInfoDisplay or autocomplete logic needs them
      }))

      if (fieldConfig.contact && fieldConfig.contact.inputProps) {
        fieldConfig.contact.inputProps.options = mappedContactOptions
      }
      // Find the newly created contact *from the mapped options list*
      const contactToSelect = freshContactsRaw.find((opt) => opt.id === newlyCreatedContactId)

      if (contactToSelect) {
        form.setFieldValue('contact', contactToSelect)
        user.value = contactToSelect
        eventBus.emit('updateMemberCount', { id: currentCompanyId, count: 1 })
      } else {
        form.setFieldValue('contact', { id: newContactData.id, name: newContactData.name })
        user.value = newContactData // Use the event data for display
      }
      updateNameKey.value++
    } catch (error) {
      console.error('[CreateTicketForm] Error re-fetching contacts or setting value:', error)
    }
  } else {
    console.warn('[CreateTicketForm] No company selected, cannot refresh contacts or select new contact effectively.')
  }
}
</script>

<template>
  <div class="flex flex-col w-full" :style="{ height: 'calc(100% - 70px)' }">
    <LoadingOverlay
      :active="isLoading && accountStore.isCustomerUser && !isDataReadyForClient"
      :can-cancel="false"
      loader="dots"
      color="#007bff"
      background-color="rgba(255, 255, 255, 0.5)"
      :is-full-page="false"
    />
    <Alert v-if="errorMessage" variant="destructive" class="!bg-destructive text-white mb-5 flex items-center">
      <TriangleAlert color="white" class="w-4 h-4" />
      <AlertDescription class="font-semibold">
        {{ errorMessage }}
      </AlertDescription>
    </Alert>

    <!-- NEW CENTRALIZED LOADER FOR COMPANY CONTACTS -->
    <div v-if="isCompanyContactLoading" class="my-4 flex justify-center items-center text-sm text-muted-foreground">
      <Loader2 class="w-5 h-5 mr-2 animate-spin" />
      <span>Loading company contacts...</span>
    </div>

    <div class="flex flex-col w-full" :class="{ 'pointer-events-none': isCompanyContactLoading }">
      <AutoForm
        class="w-full lg:w-2/5 space-y-6"
        :schema="schema"
        :field-config="fieldConfig as any"
        :dependencies="dependencies as any"
        :form="form"
      >
        <div class="h-6">
          <div
            class="flex justify-between w-full gap-2 absolute bottom-0 p-4 !rounded-b-lg right-0 border-t !border-border z-[100] bg-create-form-footer"
          >
            <div :class="{ 'opacity-50': isCompanyContactLoading }">
              <DialogCheck :show-icon="true" @dialog-should-close="handleDialogShouldClose" />
            </div>
            <slot v-bind="{ submitForm }">
              <div class="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  class="!text-primary"
                  :class="{ 'opacity-50': isCompanyContactLoading }"
                  :disabled="isLoading"
                  @click="submitAndAddAnother"
                >
                  <Loader2 v-if="isLoading" class="w-4 h-4 mr-2 animate-spin" /> Create and add another
                </Button>
                <Button type="button" :disabled="isLoading" @click="submitForm">
                  <Loader2 v-if="isLoading" class="w-4 h-4 mr-2 animate-spin" /> Create
                </Button>
              </div>
            </slot>
          </div>
        </div>
      </AutoForm>
    </div>

    <!-- Create Contact Dialog in ticket creation -->
    <ContactQuickCreateDialog
      v-if="selectedCompanyForNewContact"
      :open="showCreateContactDialog"
      :company-id="selectedCompanyForNewContact.id"
      :company-name="selectedCompanyForNewContact.name"
      @update:open="showCreateContactDialog = $event"
      @contact-created="handleContactCreated"
    />
  </div>
</template>
